/**
 * Grand Quack Auto — duck status panel.
 *
 * Bottom-left HUD panel showing the current duck tier with a rotating emoji,
 * tier name, flavor text, and mini stats. Mobile-aware: shifts above the
 * virtual joystick on touch devices.
 */

import { DUCKLING } from '../config';
import { DUCK_TIERS } from '../data/duckTiers';
import { getCurrentTierIndex } from '../systems/duckEvolutionSystem';
import { getDucklings } from '../systems/ducklingSystem';
import type { System, World } from '../types';

const FLAVOR_TEXT = [
  'Just a rubber duck...',
  'Making ripples!',
  'The pond will remember this.',
  'Never ducks a fight.',
  'An offer you can\'t refuse.',
  'Straight outta Pond.',
  'One Quack to Rule Them All.',
] as const;

let initialized = false;
let panelEl: HTMLElement | null = null;
let iconEl: HTMLElement | null = null;
let nameEl: HTMLElement | null = null;
let flavorEl: HTMLElement | null = null;
let statsEl: HTMLElement | null = null;
let lastTier = -1;

function initPanel(): void {
  initialized = true;
  panelEl = document.getElementById('duck-panel');
  if (!panelEl) return;

  // On touch devices, shift panel above the virtual joystick.
  if ('ontouchstart' in window) panelEl.style.bottom = '180px';

  panelEl.innerHTML = [
    '<div class="duck-panel-icon" id="dp-icon"></div>',
    '<div id="dp-name" style="font-weight:bold; font-size:10px; margin:2px 0;"></div>',
    '<div id="dp-flavor" style="font-size:7px; color:#ccc; margin-bottom:4px;"></div>',
    '<div id="dp-stats" style="font-size:7px; color:#aaa;"></div>',
  ].join('');

  iconEl = document.getElementById('dp-icon');
  nameEl = document.getElementById('dp-name');
  flavorEl = document.getElementById('dp-flavor');
  statsEl = document.getElementById('dp-stats');
}

export const duckPanelSystem: System = {
  name: 'duckPanel',

  render(_dt: number, _alpha: number, world: World) {
    if (!initialized) initPanel();
    if (!panelEl) return;

    const tier = getCurrentTierIndex();
    const tierData = DUCK_TIERS[tier];
    if (!tierData) return;

    // Only update tier-dependent fields when the tier changes.
    if (tier !== lastTier) {
      lastTier = tier;
      if (iconEl) iconEl.textContent = tierData.icon;
      if (nameEl) nameEl.textContent = `Lv.${tier + 1} ${tierData.name}`;
      if (flavorEl) flavorEl.textContent = FLAVOR_TEXT[tier] ?? '';
    }

    // Stats update every frame.
    if (statsEl) {
      const chaos = Math.floor(world.chaos.points);
      const combo = world.chaos.combo > 1 ? `x${world.chaos.combo}` : '-';
      const brood = `${getDucklings().length}/${DUCKLING.maxBrood}`;
      statsEl.textContent = `Chaos ${chaos} | Combo ${combo} | Brood ${brood}`;
    }
  },
};
